export const AUTO_VALUE = "auto";

export const CSS_UNIT_PATTERN = /^\d+(\.\d+)?(rem|px|em|ch|ex|vh|vw|%)$/;

/**
 * Check if a value is a valid CSS unit string
 * @param {string} value - value to check
 * @returns {boolean}
 *
 * @example
 * isCssUnit("1.5rem") // true
 * isCssUnit("24px") // true
 * isCssUnit("small") // false
 */
export function isCssUnit(value) {
  return CSS_UNIT_PATTERN.test(value);
}

export function toRem(value) {
  return value / 16;
}

export function toRemString(value) {
  if (value === AUTO_VALUE) {
    return value;
  }

  return `${toRem(value)}rem`;
}

/**
 * Parses a CSS-like padding string (e.g. "2.5rem 1rem 2.5rem 0")
 * into an object of CSS custom properties.
 *
 * Supports 1–4 values (CSS shorthand logic):
 * 1 → all sides
 * 2 → top/bottom | left/right
 * 3 → top | left/right | bottom
 * 4 → top | right | bottom | left
 *
 * @param {string} input
 * @returns {Record<string, string>}
 *
 * @example
 * parsePadding("2.5rem 1rem 2.5rem 0")
 * // returns { top: "2.5rem", right: "1rem", bottom: "2.5rem", left: "0" }
 * parsePadding("2.5rem 1rem 2.5rem")
 * // returns { top: "2.5rem", right: "1rem", bottom: "2.5rem", left: "1rem" }
 * parsePadding("2.5rem")
 * // returns { top: "2.5rem", right: "2.5rem", bottom: "2.5rem", left: "2.5rem" }
 * parsePadding("2.5rem 1rem")
 * // returns { top: "2.5rem", right: "1rem", bottom: "2.5rem", left: "1rem" }
 */
export function parsePadding(input = "") {
  const parts = input?.trim?.().split(/\s+/).filter(Boolean);

  if (!parts || parts.length === 0) {
    return {};
  }

  const all = parts[0];

  const top = all;
  const right = parts[1] ?? all;
  const bottom = parts[2] ?? all;
  const left = parts[3] ?? right;

  return {
    top,
    right,
    bottom,
    left,
  };
}

// Computed font-size of an element in px; falls back to 16 only when unresolved
// (NaN, e.g. jsdom), so a genuine 0 stays 0.
function fontSizePx(element) {
  const size = parseFloat(getComputedStyle(element).fontSize);
  return Number.isFinite(size) ? size : 16;
}

/**
 * Resolve a CSS length token (rem / em / px / unitless) to pixels. Custom
 * properties come back from getComputedStyle as their authored string, never
 * resolved, so rem/em are converted manually against the relevant font size.
 *
 * @param {string} raw - the length token, e.g. "1.5rem", "12px", "4".
 * @param {Element} [relativeTo] - element whose font-size resolves `em` units;
 *   falls back to the document root when omitted.
 * @returns {number|null} pixels, or null when the value is empty or non-numeric.
 *
 * @example
 * resolveLengthToPx("1rem")    // 16 (at a 16px root)
 * resolveLengthToPx("12px")    // 12
 * resolveLengthToPx("calc(1rem + 4px)") // null
 */
export function resolveLengthToPx(raw, relativeTo) {
  const value = raw?.trim();
  if (!value) {
    return null;
  }
  const num = parseFloat(value);
  if (!Number.isFinite(num)) {
    return null;
  }
  if (value.endsWith("rem")) {
    return num * fontSizePx(document.documentElement);
  }
  if (value.endsWith("em")) {
    return num * fontSizePx(relativeTo ?? document.documentElement);
  }
  if (value.endsWith("px")) {
    return num;
  }
  // Unitless (e.g. "4") is treated as px; any other unit (%, vh, vw, ch, …) is
  // unsupported and returns null so callers fall back to their default.
  return /^-?\d*\.?\d+$/.test(value) ? num : null;
}
